import {
  Drawer,
  Form,
  Select,
  Button,
  Space,
  Typography,
  Checkbox,
  Alert,
} from "antd";
import { useState, useMemo, useEffect } from "react";
import { useFetcher } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as APITypes from "~/API";
import { INTENTS, resourceTypeHasChildren } from "~/utils";
import { useGetEntityList } from "~/hooks";
import { getEntityListOptions } from "~/services/browser";
import type { FormattedResourceAccessRight } from "~/types";

const ALL_ENTITY_TYPES = [
  APITypes.EntityType.Property,
  APITypes.EntityType.Unit,
  APITypes.EntityType.Building,
  APITypes.EntityType.ServiceRequest,
  APITypes.EntityType.Amenity,
  APITypes.EntityType.UnitPlan,
  APITypes.EntityType.Tenant,
  APITypes.EntityType.Stay,
  APITypes.EntityType.Parcel,
];

type GrantAccessDrawerProps = {
  visible: boolean;
  onClose: () => void;
  memberID: string;
  organizationID: string;
  existingAccessRights: FormattedResourceAccessRight[];
  isAdmin: boolean;
  onSubmitStart?: () => void;
};

export const GrantAccessDrawer = ({
  visible,
  onClose,
  memberID,
  organizationID,
  existingAccessRights,
  isAdmin,
  onSubmitStart,
}: GrantAccessDrawerProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const fetcher = useFetcher();
  const [selectedResourceType, setSelectedResourceType] =
    useState<APITypes.EntityType | null>(null);

  // Get loading state from fetcher
  const loading = fetcher.state === "submitting" || fetcher.state === "loading";

  // Determine if we should fetch entity list for the selected resource type
  const shouldFetchEntityList =
    selectedResourceType === APITypes.EntityType.Property ||
    selectedResourceType === APITypes.EntityType.Building ||
    selectedResourceType === APITypes.EntityType.Unit ||
    selectedResourceType === APITypes.EntityType.ServiceRequest ||
    selectedResourceType === APITypes.EntityType.Amenity ||
    selectedResourceType === APITypes.EntityType.UnitPlan ||
    selectedResourceType === APITypes.EntityType.Parcel;

  // Fetch entity list for Property, Building, Unit, ServiceRequest, or Amenity
  const entityListQuery = useGetEntityList({
    entityType: selectedResourceType || APITypes.EntityType.Property,
    ownerID: organizationID,
    ownerType: APITypes.OwnerType.Organization,
  });

  // Format the entity list into options for the Select dropdown
  const resourceOptions = shouldFetchEntityList
    ? getEntityListOptions(entityListQuery.data)
    : [];

  const getResourceTypeLabel = (resourceType: APITypes.EntityType) => {
    return t(resourceType as any) || resourceType;
  };

  // Check what access the selected resource already has
  const existingAccessRecord = useMemo(() => {
    const resourceID = form.getFieldValue("resourceID");
    if (!resourceID) return null;

    // Find the access record for this resource
    return (
      existingAccessRights.find((right) => right.resourceID === resourceID) ||
      null
    );
  }, [existingAccessRights, form.getFieldValue("resourceID")]);

  const handleSubmit = async (values: any) => {
    const { resourceType, resourceID, accessTypes } = values;

    // Admin users grant access (status = Granted), non-admins request access (status = Requested)
    const readStatus = accessTypes?.includes("Read")
      ? isAdmin
        ? APITypes.ResourceAccessRightStatus.Granted
        : APITypes.ResourceAccessRightStatus.Requested
      : APITypes.ResourceAccessRightStatus.NotGranted;

    const writeStatus = accessTypes?.includes("Write")
      ? isAdmin
        ? APITypes.ResourceAccessRightStatus.Granted
        : APITypes.ResourceAccessRightStatus.Requested
      : APITypes.ResourceAccessRightStatus.NotGranted;

    // Check if children access is selected
    const applyReadToChildren = accessTypes?.includes("ReadChildren") || false;
    const applyWriteToChildren =
      accessTypes?.includes("WriteChildren") || false;

    const formData = new FormData();
    formData.append("intent", INTENTS._add_instance_access);
    formData.append(
      "body",
      JSON.stringify({
        resourceType,
        resourceID,
        readAccessStatus: readStatus,
        writeAccessStatus: writeStatus,
        existingRecordID: existingAccessRecord?.id,
        existingRecordVersion: existingAccessRecord?._version,
        applyReadToChildren,
        applyWriteToChildren,
      })
    );

    // Notify parent that submission is starting
    if (onSubmitStart) {
      onSubmitStart();
    }

    fetcher.submit(formData, {
      method: "post",
      action: window.location.pathname,
    });

    // Close drawer and reset form after submission
    form.resetFields();
    setSelectedResourceType(null);
    onClose();
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedResourceType(null);
    onClose();
  };

  const selectedResourceID = Form.useWatch("resourceID", form);
  const accessTypes = Form.useWatch("accessTypes", form);

  // Check if children checkboxes are selected
  const hasChildrenAccessSelected =
    accessTypes?.includes("ReadChildren") ||
    accessTypes?.includes("WriteChildren");

  // Pre-check existing access when resource is selected
  useEffect(() => {
    if (existingAccessRecord && selectedResourceID) {
      const existingAccess = [];
      // For admins, show Granted as pre-checked. For non-admins, show Granted or Requested
      if (
        existingAccessRecord.readAccessStatus ===
          APITypes.ResourceAccessRightStatus.Granted ||
        (!isAdmin &&
          existingAccessRecord.readAccessStatus ===
            APITypes.ResourceAccessRightStatus.Requested)
      )
        existingAccess.push("Read");
      if (
        existingAccessRecord.writeAccessStatus ===
          APITypes.ResourceAccessRightStatus.Granted ||
        (!isAdmin &&
          existingAccessRecord.writeAccessStatus ===
            APITypes.ResourceAccessRightStatus.Requested)
      )
        existingAccess.push("Write");
      form.setFieldValue("accessTypes", existingAccess);
    } else if (selectedResourceID) {
      // Clear when selecting a new resource without existing access
      form.setFieldValue("accessTypes", []);
    }
  }, [existingAccessRecord, selectedResourceID, form, isAdmin]);

  // Prevent accidental browser close during submission
  useEffect(() => {
    if (loading) {
      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        e.preventDefault();
        e.returnValue = "";
        return "";
      };

      window.addEventListener("beforeunload", handleBeforeUnload);
      return () =>
        window.removeEventListener("beforeunload", handleBeforeUnload);
    }
  }, [loading]);

  const title = isAdmin ? t("Grant_New_Access") : t("Request_New_Access");
  const buttonText = isAdmin ? t("Grant_Access") : t("Request_Access");

  return (
    <Drawer
      title={title}
      open={visible}
      onClose={handleCancel}
      width={500}
      footer={
        <div style={{ textAlign: "right" }}>
          <Space>
            <Button onClick={handleCancel} disabled={loading}>
              {t("Cancel")}
            </Button>
            <Button
              type="primary"
              onClick={() => form.submit()}
              loading={loading}
            >
              {buttonText}
            </Button>
          </Space>
        </div>
      }
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="resourceType"
          label={t("Resource_Type")}
          rules={[
            {
              required: true,
              message: t("Please_select_resource_type"),
            },
          ]}
        >
          <Select
            placeholder={t("Select_Resource_Type")}
            onChange={(value) => {
              setSelectedResourceType(value);
              // Clear the resourceID field when resource type changes
              form.setFieldValue("resourceID", undefined);
            }}
            options={ALL_ENTITY_TYPES.map((type) => ({
              label: getResourceTypeLabel(type),
              value: type,
            }))}
          />
        </Form.Item>

        {selectedResourceType && (
          <>
            <Form.Item
              name="resourceID"
              label={t("Select_Resource")}
              rules={[
                {
                  required: true,
                  message: t("Please_select_resource"),
                },
              ]}
            >
              <Select
                placeholder={t("Select_Resource")}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                loading={shouldFetchEntityList && entityListQuery.isLoading}
                disabled={!shouldFetchEntityList}
                notFoundContent={
                  shouldFetchEntityList && resourceOptions.length === 0
                    ? t("No_Resources_Found")
                    : undefined
                }
                options={
                  shouldFetchEntityList
                    ? resourceOptions.map((option) => ({
                        label: option.text,
                        value: option.value,
                      }))
                    : [
                        {
                          label: `${t("Coming_Soon")} - ${getResourceTypeLabel(
                            selectedResourceType
                          )}`,
                          value: "temp-id-coming-soon",
                        },
                      ]
                }
              />
            </Form.Item>

            <Form.Item
              name="accessTypes"
              label={t("Access_Type")}
              rules={[
                {
                  required: true,
                  message: t("Please_select_access_type"),
                },
              ]}
            >
              <Checkbox.Group>
                <Space direction="vertical">
                  <Checkbox
                    value="Read"
                    disabled={
                      existingAccessRecord?.readAccessStatus ===
                        APITypes.ResourceAccessRightStatus.Granted ||
                      (!isAdmin &&
                        existingAccessRecord?.readAccessStatus ===
                          APITypes.ResourceAccessRightStatus.Requested)
                    }
                  >
                    {t("Read")}
                    {existingAccessRecord?.readAccessStatus ===
                      APITypes.ResourceAccessRightStatus.Granted && " ✓"}
                    {!isAdmin &&
                      existingAccessRecord?.readAccessStatus ===
                        APITypes.ResourceAccessRightStatus.Requested &&
                      " ⏳"}
                  </Checkbox>
                  <Checkbox
                    value="Write"
                    disabled={
                      existingAccessRecord?.writeAccessStatus ===
                        APITypes.ResourceAccessRightStatus.Granted ||
                      (!isAdmin &&
                        existingAccessRecord?.writeAccessStatus ===
                          APITypes.ResourceAccessRightStatus.Requested)
                    }
                  >
                    {t("Write")}
                    {existingAccessRecord?.writeAccessStatus ===
                      APITypes.ResourceAccessRightStatus.Granted && " ✓"}
                    {!isAdmin &&
                      existingAccessRecord?.writeAccessStatus ===
                        APITypes.ResourceAccessRightStatus.Requested &&
                      " ⏳"}
                  </Checkbox>

                  {/* Children access checkboxes - only show for resources with children */}
                  {resourceTypeHasChildren(selectedResourceType) && (
                    <>
                      <Typography.Text
                        type="secondary"
                        style={{ fontSize: "12px", marginTop: "8px" }}
                      >
                        Grant access to all nested resources:
                      </Typography.Text>
                      <Checkbox
                        value="ReadChildren"
                        disabled={!accessTypes?.includes("Read")}
                      >
                        {t("Read_for_all_children")}
                      </Checkbox>
                      <Checkbox
                        value="WriteChildren"
                        disabled={!accessTypes?.includes("Write")}
                      >
                        {t("Write_for_all_children")}
                      </Checkbox>
                    </>
                  )}
                </Space>
              </Checkbox.Group>
            </Form.Item>

            {/* Warning alert for resources with children */}
            {resourceTypeHasChildren(selectedResourceType) &&
              hasChildrenAccessSelected && (
                <Alert
                  type="warning"
                  message={t("Children_Access_Warning")}
                  description={t("Children_Access_Warning_Description")}
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}
          </>
        )}
      </Form>
    </Drawer>
  );
};
